<script setup lang="ts">
    import { requireIsAdministrator as vRequireIsAdministrator } from '@/shared/directives/requirePermission';
    import { LibraryPolicy } from '@/shared/helpers/api';
    import ViewBaseAccessPolicy from '@/apps/config/AccessPolicy/ViewBaseAccessPolicy.vue';
    import AccessPolicyPicker from '@/apps/config/AccessPolicy/AccessPolicyPicker.vue';
    import { filterAfterAdd } from '@/apps/config/AccessPolicy/filterOptions';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { computed, onMounted, Ref, ref, watch } from 'vue';
    import { useAccessPolicyStore } from '@/stores/accessPolicyStore';
    import useShow from '@/shared/composables/useShow';

    const loading: Ref<boolean> = ref(false);
    const saving: Ref<boolean> = ref(false);

    const showCreateNewModal: Ref<boolean> = ref(false);

    const librarySearch = ref('');
    /** What the user last typed in the library picker; restored after each Add. */
    const libraryFilter = ref('');

    const store = useAccessPolicyStore();
    const { showError, showInfo } = useShow();

    const isValidPolicy = computed((): boolean => store.isValidLibraryPolicy());

    const systemNames = computed(() => store.policySystems.map(s => s.name));
    const roleNames = computed(() => store.systemRoles.map(r => r.name));

    watch(librarySearch, text => {
        if (!text) {
            libraryFilter.value = '';
        }
    });

    onMounted(async () => {
        loading.value = true;
        try {
            await Promise.all([
                store.loadSystems(),
                store.loadLibraryPolicies(),
                store.loadLibraryNames()
            ]);
        } catch {
            showError('Unable to load data');
        } finally {
            loading.value = false;
        }
    });

    async function onClickRemove(library: LibraryPolicy) {
        await store.deleteLibraryPolicy(library);
        try {
            await store.loadLibraryPolicies();
        } catch {
            showError('Unable to reload library policies');
        }
    }

    function onLibraryTyping(text: string) {
        libraryFilter.value = text;
    }

    async function onClickSave() {
        if (!store.isValidLibraryPolicy()) {
            showError('Select a library, system and role');
            return;
        }

        if (store.checkIfLibraryPolicyExists()) {
            showError('Library policy already exists');
            return;
        }

        saving.value = true;
        try {
            const addedLibrary = store.selectedLibrary;
            const saved = await store.saveLibraryPolicy().catch(() => false);
            if (!saved) {
                showError('Unexpected error: Unable to save policy');
                return;
            }

            showInfo(`Library policy added for ${addedLibrary}`);

            // Keep system, role and the typed filter so the next library is a couple of clicks away
            const filter = filterAfterAdd(libraryFilter.value, addedLibrary);
            store.setSelectedLibrary(null);
            librarySearch.value = filter;
            libraryFilter.value = filter;

            await store.loadLibraryPolicies().catch(() => showError('Unable to reload library policies'));
        } finally {
            saving.value = false;
        }
    }

    function onOpen() {
        store.clearSelection();
        librarySearch.value = '';
        libraryFilter.value = '';
        showCreateNewModal.value = true;
    }

    function onCancel() {
        showCreateNewModal.value = false;
        store.clearSelection();
    }
</script>

<template>
    <view-base-access-policy>
        <template #controls>
            <div class="level">
                <div class="level-left mt-2 mb-3">
                    <b-button
                        v-require-is-administrator
                        class="is-info"
                        @click="onOpen">
                        <span class="icon is-small">
                            <i class="mdi mdi-plus" />
                        </span>
                        <span>
                            Create new policy
                        </span>
                    </b-button>
                </div>
            </div>
        </template>
        <template #modal>
            <modal-base
                v-if="showCreateNewModal"
                @onCancel="onCancel">
                <template #modal-header>
                    <b>Add a new Library Policy</b>
                </template>
                <template #modal-body>
                    <b-field
                        label="Library"
                        horizontal>
                        <access-policy-picker
                            v-model:search="librarySearch"
                            :model-value="store.selectedLibrary ?? null"
                            :options="store.libraryNames"
                            placeholder="Type to filter libraries"
                            @typing="onLibraryTyping"
                            @update:model-value="store.setSelectedLibrary" />
                    </b-field>

                    <b-field
                        label="System"
                        horizontal>
                        <access-policy-picker
                            :model-value="store.selectedSystem?.name ?? null"
                            :options="systemNames"
                            placeholder="Select system"
                            @update:model-value="store.setSelectedSystem" />
                    </b-field>

                    <b-field
                        label="Role"
                        horizontal>
                        <access-policy-picker
                            :model-value="store.selectedRole?.name ?? null"
                            :options="roleNames"
                            :disabled="roleNames.length === 0"
                            placeholder="Select a Role"
                            @update:model-value="store.setSelectedRole" />
                    </b-field>

                    <b-field
                        horizontal
                        label="Result">
                        <p v-if="isValidPolicy">
                            Users from <strong>{{ store.selectedSystem?.name }}</strong> with
                            <strong>{{ store.selectedRole?.name }}</strong> will have access to
                            <strong>{{ store.selectedLibrary }}</strong>
                        </p>
                    </b-field>
                </template>
                <template #modal-footer>
                    <b-field
                        grouped
                        position="right">
                        <b-button
                            label="Close"
                            @click="onCancel" />
                        <b-button
                            label="Add"
                            type="is-primary"
                            :loading="saving"
                            :disabled="!isValidPolicy || saving"
                            @click="onClickSave" />
                    </b-field>
                </template>
            </modal-base>
        </template>
        <template #librarypolicy>
            <b-table
                :data="store.libraryPolicies"
                :loading="loading"
                default-sort="libraryName"
                striped
                narrowed
                hoverable>
                <b-table-column
                    label="Library"
                    field="libraryName"
                    sortable
                    searchable>
                    <template #searchable="props">
                        <b-autocomplete
                            v-model="props.filters.libraryName"
                            placeholder="Filter by library"
                            :data="store.libraryNames"
                            open-on-focus
                            clearable>
                            <template #default="{ option }">
                                {{ option }}
                            </template>
                        </b-autocomplete>
                    </template>
                    <template #default="props">
                        {{ props.row.libraryName }}
                    </template>
                </b-table-column>

                <b-table-column
                    field="system"
                    label="System"
                    sortable
                    searchable>
                    <template #searchable="props">
                        <b-autocomplete
                            v-model="props.filters.system"
                            placeholder="Filter by system"

                            :data="store.policySystems.map(s => s.name)"
                            open-on-focus
                            clearable>
                            <template #default="{ option }">
                                {{ option }}
                            </template>
                        </b-autocomplete>
                    </template>
                    <template #default="props">
                        {{ props.row.system }}
                    </template>
                </b-table-column>
                <b-table-column
                    field="role"
                    label="Role"
                    sortable
                    searchable>
                    <template #searchable="props">
                        <b-autocomplete
                            v-model="props.filters.role"
                            placeholder="Filter by role"
                            :data="store.systemRoles?.map(r => r.name)"
                            open-on-focus
                            clearable>
                            <template #default="{ option }">
                                {{ option }}
                            </template>
                        </b-autocomplete>
                    </template>
                    <template #default="props">
                        {{ props.row.role }}
                    </template>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    label="Explanation">
                    User(s) with role <b>{{ props.row.role }}</b>
                    from system <b>{{ props.row.system }}</b> is GRANTED ACCESS
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    label="Remove"
                    sortable>
                    <a @click="onClickRemove(props.row)">
                        <b-icon
                            icon="trash-can" />
                    </a>
                </b-table-column>
            </b-table>
        </template>
    </view-base-access-policy>
</template>
